import React, { useState, useEffect, useMemo } from "react";
import {
  MDBContainer,
  MDBIcon,
} from "mdb-react-ui-kit";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from "chart.js";

import { api, setTenantApiScope } from "../services";
import { format, subDays, subMonths, subYears } from "date-fns";
import { useLocation, useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useTranslation } from "react-i18next";
import Toolbar from "./Toolbar";
import OrdersGraph from "../Dashboard Components/OrdersCard";
import InventoryCard from "../Dashboard Components/InventoryCard";
import MonthlySaleCard from "../Dashboard Components/MonthlySalesCard";
import KPICards from "../Dashboard Components/KPICards";
import TopProductsTable from "../Dashboard Components/TopProductsTable";
import { buildDashboardApiParams } from "../utils/dashboardApiParams";
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
);

function getDefaultDashboardDateRange() {
  const today = new Date();
  return {
    dateFrom: format(subDays(today, 6), "yyyy-MM-dd"),
    dateTo: format(today, "yyyy-MM-dd"),
  };
}

function getAllDataDashboardDateRange() {
  const today = new Date();
  return {
    dateFrom: format(subYears(today, 5), "yyyy-MM-dd"),
    dateTo: format(today, "yyyy-MM-dd"),
  };
}

function Dashboard() {
  const { t, i18n } = useTranslation("translation");
  const isArabic = i18n.language === "ar";
  const location = useLocation();
  const [data, setStats] = useState();
  const [error, setError] = useState({ stats: null, chart: null });
  const [loading, setLoading] = useState(true);
  const [selectedRangeId, setSelectedRangeId] = useState("7d");
  const [dateRange, setDateRange] = useState(getDefaultDashboardDateRange);
  const [selectedPlatform, setSelectedPlatform] = useState("");
  const [storesOptions, setStoresOptions] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [storesPayload, setStoresPayload] = useState(null);
  const [storesList, setStoresList] = useState([]);
  const [branchesOptions, setBranchesOptions] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [categoriesOptions, setCategoriesOptions] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [categorySearch, setCategorySearch] = useState("");
  const [debouncedCategorySearch, setDebouncedCategorySearch] = useState("");
  const [rawPlatforms, setRawPlatforms] = useState([]);
  const placeholders = {
    dateRange: isArabic ? "نطاق التاريخ" : "Date range",
    brand: isArabic ? "العلامة التجارية" : "Brand",
    branch: isArabic ? "الفرع" : "Branch",
    category: isArabic ? "الفئة" : "Category",
    platform: isArabic ? "المنصة" : "Platform",
  };

  if (location.state?.token) {
    console.log(location.state?.token);
    localStorage.setItem("token", location.state?.token);
  }

  useEffect(() => {
    fetchDashboardStats(dateRange);
    fetchStores();
    fetchPlatforms();
  }, []);

  useEffect(() => {
    const h = setTimeout(() => {
      setDebouncedCategorySearch((categorySearch || "").trim());
    }, 700);
    return () => clearTimeout(h);
  }, [categorySearch]);

  useEffect(() => {
    if (!selectedStoreId) return;
    fetchCategoriesForStore(selectedStoreId, debouncedCategorySearch);
  }, [selectedStoreId, debouncedCategorySearch]);

  useEffect(() => {
    setTenantApiScope(selectedStoreId || null, selectedBranchId || null);
  }, [selectedStoreId, selectedBranchId]);

  useEffect(() => {
    fetchDashboardStats(dateRange);
  }, [selectedStoreId, selectedBranchId, selectedCategoryId]);

  const fetchDashboardStats = async (range, platformOverride) => {
    try {
      const token = localStorage.getItem("token");
      const df = (range?.dateFrom || "").trim();
      const dt = (range?.dateTo || "").trim();
      const platformParam = platformOverride ?? selectedPlatform;
      const response = await api.get("/dashboard", {
        headers: { Authorization: `Bearer ${token}` },
        params: buildDashboardApiParams({
          dateFrom: df && dt ? df : undefined,
          dateTo: df && dt ? dt : undefined,
          brandId: selectedStoreId || undefined,
          branchId: selectedBranchId || undefined,
          categoryId: selectedCategoryId || undefined,
          platform: platformParam || undefined,
        }),
      });
      setStats(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching dashboard stats:", error.message);
      setError((prev) => ({ ...prev, stats: error.message }));
      setLoading(false);
    }
  };

  const computeRangeFromPreset = (id) => {
    const today = new Date();
    const dateTo = format(today, "yyyy-MM-dd");
    let from;
    switch (id) {
      case "7d":
        from = subDays(today, 6);
        break;
      case "14d":
        from = subDays(today, 13);
        break;
      case "1m":
        from = subMonths(today, 1);
        break;
      case "3m":
        from = subMonths(today, 3);
        break;
      case "6m":
        from = subMonths(today, 6);
        break;
      case "1y":
        from = subMonths(today, 12);
        break;
      default:
        from = subDays(today, 6);
        break;
    }
    const dateFrom = format(from, "yyyy-MM-dd");
    return { dateFrom, dateTo };
  };

  const fetchPlatforms = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get("/platforms", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data && response.data.success && response.data.data) {
        setRawPlatforms(response.data.data.platforms || []);
      }
    } catch (error) {
      console.error("Error fetching platforms:", error);
    }
  };

  const fetchStores = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get("/stores/my", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStoresPayload(response.data);
      const stores = response.data?.stores || [];
      setStoresList(stores);
      const options = stores.map((s) => ({
        _id: s?._id,
        name: {
          en: s?.name || s?.name_en || s?.displayName || "",
          ar: s?.name_ar || s?.displayNameAr || s?.name || "",
        },
      }));
      setStoresOptions(options);
    } catch (err) {
      console.error(
        "Error fetching stores:",
        err.response?.data || err.message
      );
      setStoresOptions([]);
    }
  };

  const updateBranchesForStore = (storeId) => {
    const store = storesList.find((s) => s?._id === storeId);
    const branches = store?.branches || [];
    const options = branches.map((b) => ({
      _id: b?._id,
      name: {
        en: b?.name?.en || b?.name || b?.displayName || "",
        ar: b?.name?.ar || b?.name_ar || b?.displayNameAr || b?.name || "",
      },
    }));
    setBranchesOptions(options);
  };

  const fetchCategoriesForStore = async (storeId, searchTerm = "") => {
    if (!storeId) {
      setCategoriesOptions([]);
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      params.set("page", "1");
      params.set("limit", "100");
      params.set("storeId", storeId);
      if ((searchTerm || "").trim())
        params.set("search", (searchTerm || "").trim());

      const response = await api.get(`/categories?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const cats = Array.isArray(response.data?.categories)
        ? response.data.categories
        : Array.isArray(response.data?.data?.categories)
        ? response.data.data.categories
        : [];

      const options = cats.map((c) => ({
        _id: c?._id,
        name: {
          en: c?.name?.en || c?.name || c?.displayName || "",
          ar: c?.name?.ar || c?.name_ar || c?.displayNameAr || c?.name || "",
        },
      }));
      setCategoriesOptions(options);
    } catch (err) {
      console.error(
        "Error fetching categories:",
        err.response?.data || err.message
      );
      setCategoriesOptions([]);
    }
  };

  const dateRangeOptions = [
    { _id: "7d", name: { en: "7 Days", ar: "٧ أيام" } },
    { _id: "14d", name: { en: "14 Days", ar: "١٤ يوم" } },
    { _id: "1m", name: { en: "1 Month", ar: "شهر واحد" } },
    { _id: "3m", name: { en: "3 Months", ar: "٣ أشهر" } },
    { _id: "6m", name: { en: "6 Months", ar: "٦ أشهر" } },
    { _id: "1y", name: { en: "1 Year", ar: "سنة واحدة" } },
  ];

  const platformOptions = useMemo(() => {
    const options = [];
    if (rawPlatforms.length > 0) {
      rawPlatforms.forEach((p) => {
        options.push({
          _id: p.key,
          name: { en: p.nameEn, ar: p.nameAr },
        });
      });
    }
    return options;
  }, [rawPlatforms]);

  if (loading) {
    return (
      <div className="dashboard-page-root oh-page">
        <MDBContainer fluid className="content-area flex-grow-1">
          <div className="oh-dashboard-stack">
            <div className="oh-dashboard-header">
              <Skeleton height={40} width={220} />
              <Skeleton height={32} width="100%" style={{ marginTop: 12 }} />
            </div>
            <div className="oh-dashboard-grid oh-dashboard-grid--2">
              <Skeleton height={301} borderRadius={12} />
              <Skeleton height={301} borderRadius={12} />
            </div>
            <div className="oh-dashboard-grid oh-dashboard-grid--split">
              <Skeleton height={300} borderRadius={12} />
              <Skeleton height={300} borderRadius={12} />
            </div>
            <Skeleton height={320} borderRadius={10} />
          </div>
        </MDBContainer>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="dashboard-page-root oh-page">
        <MDBContainer fluid className="content-area flex-grow-1 text-center my-5">
          <MDBIcon icon="exclamation-triangle" size="2x" />
          <p>Failed to load data</p>
        </MDBContainer>
      </div>
    );
  }
  return (
    <div className="dashboard-page-root oh-page">
      <MDBContainer fluid className="content-area flex-grow-1">
        <h1 className="page-title-header">
          {t("dashboardPage.pageTitle")}
        </h1>

        <div className="oh-dashboard-stack">
          <div className="oh-dashboard-header">
            <Toolbar
              toolbarVariant="dashboard"
              extraDropdowns={[
                {
                  placeholder: placeholders.dateRange,
                  options: dateRangeOptions,
                  selectedOption: selectedRangeId,
                  handleSelect: (opt) => {
                    if (!opt?._id) {
                      setSelectedRangeId(null);
                      const clearedRange = getAllDataDashboardDateRange();
                      setDateRange(clearedRange);
                      fetchDashboardStats(clearedRange);
                      return;
                    }
                    const id = opt._id;
                    setSelectedRangeId(id);
                    const range = computeRangeFromPreset(id);
                    setDateRange(range);
                    fetchDashboardStats(range);
                  },
                },
                {
                  placeholder: placeholders.brand,
                  options: storesOptions,
                  selectedOption: selectedStoreId || null,
                  handleSelect: (opt) => {
                    const id = opt?._id || "";
                    setSelectedStoreId(id);
                    updateBranchesForStore(id);
                    setSelectedBranchId("");
                    fetchCategoriesForStore(id, "");
                    setSelectedCategoryId("");
                  },
                },
                {
                  placeholder: placeholders.branch,
                  options: branchesOptions,
                  selectedOption: selectedBranchId || null,
                  disabled: false,
                  emptyMessage: !selectedStoreId
                    ? t("Select a brand first")
                    : t("No options found"),
                  handleSelect: (opt) => {
                    const id = opt?._id || "";
                    setSelectedBranchId(id);
                  },
                },
                {
                  placeholder: placeholders.category,
                  options: categoriesOptions,
                  selectedOption: selectedCategoryId || null,
                  disabled: false,
                  emptyMessage:
                    !selectedStoreId || !selectedBranchId
                      ? t("Select a brand first")
                      : t("No options found"),
                  onSearchChange: (term) => setCategorySearch(term),
                  handleSelect: (opt) => {
                    const id = opt?._id || "";
                    setSelectedCategoryId(id);
                  },
                },
                {
                  placeholder: placeholders.platform,
                  options: platformOptions,
                  selectedOption: selectedPlatform || null,
                  handleSelect: (opt) => {
                    const id = (opt?._id || "").toUpperCase();
                    setSelectedPlatform(id);
                    fetchDashboardStats(dateRange, id);
                  },
                },
              ]}
            />
          </div>

          <div className="oh-dashboard-grid oh-dashboard-grid--2">
            <div className="oh-dashboard-grid__cell">
              <OrdersGraph data={data} platforms={rawPlatforms} />
            </div>
            <div className="oh-dashboard-grid__cell">
              <InventoryCard
                brandId={selectedStoreId || null}
                branchId={selectedBranchId || null}
                categoryId={selectedCategoryId || null}
              />
            </div>
          </div>

          <div className="oh-dashboard-grid oh-dashboard-grid--split">
            <div className="oh-dashboard-grid__cell">
              <MonthlySaleCard monthlySale={data?.monthlySale} />
            </div>
            <div className="oh-dashboard-grid__cell">
              <KPICards kpis={data?.kpis} rangeId={selectedRangeId} />
            </div>
          </div>

          <TopProductsTable topProducts={data?.topProducts} />
        </div>
      </MDBContainer>
    </div>
  );
}

export default Dashboard;
